/**
 * Persistence for the offline mail mirror.
 *
 * Web reload-durability needs a store that survives a page kill: IndexedDB
 * (available in the browser + Tauri WebView). Unit tests and any SSR/no-DB
 * context fall back to an in-memory store. The mirror is serialized through the
 * model's versioned `serialize`/`deserialize`, so a schema bump or a corrupt
 * blob simply yields `null` (→ a fresh mirror) instead of crashing boot.
 *
 * Keyed by the mail account id, so each connected account keeps an isolated
 * mirror and the per-tab active-account selection never crosses streams.
 */

import { deserialize, type MailboxMirror, serialize } from "./mirror";

export interface MirrorStore {
  load(accountId: string): Promise<MailboxMirror | null>;
  save(mirror: MailboxMirror): Promise<void>;
  remove(accountId: string): Promise<void>;
}

const DB_NAME = "cartero-mail-mirror";
const STORE = "mailboxes";
const DB_VERSION = 1;

interface StoredRecord {
  accountId: string;
  blob: string;
}

/** In-memory store — deterministic, used by tests and no-IndexedDB runtimes. */
export function createMemoryMirrorStore(): MirrorStore {
  const map = new Map<string, string>();
  return {
    load: (accountId) => {
      const raw = map.get(accountId);
      return Promise.resolve(raw === undefined ? null : deserialize(raw));
    },
    save: (mirror) => {
      map.set(mirror.accountId, serialize(mirror));
      return Promise.resolve();
    },
    remove: (accountId) => {
      map.delete(accountId);
      return Promise.resolve();
    },
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.addEventListener("upgradeneeded", () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "accountId" });
      }
    });
    req.addEventListener("success", () => resolve(req.result));
    req.addEventListener("error", () => reject(req.error ?? new Error("indexedDB.open failed")));
  });
}

/** Awaits a transaction's terminal event, rejecting on error/abort. */
function awaitTx(tx: IDBTransaction, label: string): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.addEventListener("complete", () => resolve());
    tx.addEventListener("error", () => reject(tx.error ?? new Error(label)));
    tx.addEventListener("abort", () => reject(tx.error ?? new Error(label)));
  });
}

/** IndexedDB-backed store. Each op opens/uses a short-lived transaction. */
function createIdbMirrorStore(): MirrorStore {
  return {
    async load(accountId) {
      const db = await openDb();
      try {
        const record = await new Promise<StoredRecord | undefined>((resolve, reject) => {
          const tx = db.transaction(STORE, "readonly");
          const req = tx.objectStore(STORE).get(accountId);
          req.addEventListener("success", () => {
            // Trust boundary: IDB hands back `any`; the stored shape is our own.
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            resolve(req.result as StoredRecord | undefined);
          });
          req.addEventListener("error", () => reject(req.error ?? new Error("mirror load failed")));
        });
        return record ? deserialize(record.blob) : null;
      } finally {
        db.close();
      }
    },
    async save(mirror) {
      const db = await openDb();
      try {
        const tx = db.transaction(STORE, "readwrite");
        const record: StoredRecord = { accountId: mirror.accountId, blob: serialize(mirror) };
        tx.objectStore(STORE).put(record);
        await awaitTx(tx, "mirror save failed");
      } finally {
        db.close();
      }
    },
    async remove(accountId) {
      const db = await openDb();
      try {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).delete(accountId);
        await awaitTx(tx, "mirror remove failed");
      } finally {
        db.close();
      }
    },
  };
}

let singleton: MirrorStore | null = null;

/** The process-wide mirror store: IndexedDB when present, memory otherwise. */
export function getMirrorStore(): MirrorStore {
  if (singleton) {
    return singleton;
  }
  singleton = typeof indexedDB !== "undefined" ? createIdbMirrorStore() : createMemoryMirrorStore();
  return singleton;
}
